import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ReferenceLine, Tooltip, Legend, ResponsiveContainer, Label, Brush, CartesianGrid } from 'recharts';
import { japaneseTickFormatter } from '../utils/commonUtils';
import TimeRangeContainer from '../utils/TimeRangeContainer';
import Loading from '../Loading';
import AxisSettingButton from '../utils/AxisSettingButton';
import NotebookIcon from '../NotebookIcon';
import TwoColumnSwitch from '../utils/TwoColumnSwitch';
import { fetchQuarterlyFinancialData } from '../api/api_calls';
import { useNavigate,useLocation } from 'react-router-dom';


const AnalysisBarGraph = ({ data, companies, handleBookmarkClick, chatHistoryId, parentWidth, showSaveNotebookIcon=true, showTwoColumnSwitch=true, height = 500 }) => {
    const [isSmallArea, setIsSmallArea] = useState(false);
    const [selectedRange, setSelectedRange] = useState('5Y');
    const [selectedPeriod, setSelectPeriod] = useState('annual')
    const [modifiedData, setModifiedData] = useState(null);
    const [cleanedData, setCleanedData] = useState(null);
    const [state, setState] = useState('auto');
    const [annualData, setAnnualData] = useState(data);
    const [quarterlyData, setQuarterlyData] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (selectedPeriod === 'annual') {
            setModifiedData(adjustTimeRange(annualData));
        } else {
            setModifiedData(adjustTimeRangeQuarter(quarterlyData));
        }
    }, [selectedRange])

    useEffect(() => {
        if (selectedPeriod === 'annual') {
            setModifiedData(adjustTimeRange(annualData));
        }
    }, [selectedPeriod])
    
    useEffect(() => {
        if (modifiedData) {
            const data = cleanData(modifiedData.data)
            setCleanedData(data)
        }
    }, [modifiedData])

    const MAX_NUM_OF_DATA = 5;
    const stopColor = [
        { start: 'rgba(145, 186, 250, 1)', end: 'rgba(80, 140, 200, 1)' }, // 青
        { start: 'rgba(90, 195, 90, 1)', end: 'rgba(50, 110, 50, 1)' }, // 緑
        { start: 'rgba(255, 120, 90, 1)', end: 'rgba(225, 97, 50, 1)' }, // オレンジ
        { start: 'rgba(147, 112, 219, 1)', end: 'rgba(75, 0, 130, 1)' },  // 紫
        { start: 'rgba(255, 235, 59, 1)', end: 'rgba(251, 192, 45, 1)' }, // 黄色
    ];

    const cleanData = (data) => {
        // 年の数値部分だけを抽出して昇順に並び替える
        const sortedData = [...data].sort((a, b) => parseInt(a.x) - parseInt(b.x));
        // NaN となる部分を 'N/A' に置き換える処理をすべてのキーに対して行う
        const cleanedData = sortedData.map(item => {
            const cleanedItem = { ...item };
            
            // 'x' キー以外のすべてのキーに対して処理
            Object.keys(cleanedItem).forEach(key => {
                if (key !== 'x' && (cleanedItem[key] === undefined || cleanedItem[key] === null)) {
                    cleanedItem[key] = 'N/A'; // データがない場合は 'N/A' に置き換え
                }
            });
            return cleanedItem;
        });
        return cleanedData;
    };

    const CustomLabel = ({ x, y, width, value, index, secCode }) => {
        const offset = value >= 0 ? -10 : 20;
        if (value === 'N/A') {
            return null;
        } else {
            return (
                <text
                  x={x + width / 2}
                  y={y + offset} 
                  fill="black"
                  textAnchor="middle"
                  fontSize={14}
                >
                  { cleanedData[index][secCode] ? (cleanedData[index][secCode] / data['divUnit'][secCode]).toFixed(1) : ""}
                  { cleanedData[index][secCode] ?data['unit'][secCode] : ""}
                </text>
            );    
        }
        
    };
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip px-3 py-2 border border-gray-400 bg-gray-600 rounded shadow text-white">
                    <div>{`${payload[0].payload.x}${data['unitX']}`}</div>
                    {payload.map((item, index) => {
                        const seriesIndex = companies.findIndex(company => company.secCode === item.dataKey);
                        const labelColor = stopColor[seriesIndex]?.start || 'rgba(100, 100, 100, 0.7)';
                        return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                                <svg width="12" height="12" className="flex-shrink-0">
                                    <rect
                                        width="12"
                                        height="12"
                                        fill={labelColor}
                                        rx="2"
                                    />
                                </svg>
                                <div className="text-white m-1 ml-2">
                                    {`${item.name}: ${(item.payload[item['dataKey']] / data['divUnit'][item['dataKey']]).toFixed(1)}${data['unit'][item['dataKey']]}`}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };
      
    const timeRanges = [
        { label: '3Y', value: '3Y' },
        { label: '5Y', value: '5Y' },
        { label: '10Y', value: '10Y' },
        { label: 'MAX', value: 'MAX' },
    ];

    const handleTimeRangeSetting = (newRange) => {
        setSelectedRange(newRange);
    }
    const filterQuarterlyDataByYears = (data, years) => {
        const currentYear = new Date().getFullYear(); // 現在の年を取得
        const startYear = currentYear - years + 1; // 抽出開始年を計算
    
        // 年ごとのフィルタリング
        const filteredData = data.filter((quarter) => {
            const year = parseInt(quarter.x.split(' ')[0], 10); // "YYYY Q?" のYYYY部分を取得
            return year >= startYear;
        });
    
        return filteredData;
    };
    
    const adjustTimeRange = (data) => {
        if (!data || data.length === 0) return [];
        let modifiedData;
        switch (selectedRange) {
            case '3Y':
                modifiedData = data.data.length > 3
                ? data.data.slice(data.data.length - 3, data.data.length) 
                : data.data;
                return {...data, data: modifiedData}
            case '5Y': 
                modifiedData = data.data.length > 5 
                ? data.data.slice(data.data.length - 5, data.data.length) 
                : data.data;
                return {...data, data: modifiedData}
            case '10Y': 
                modifiedData = data.data.length > 10 
                ? data.data.slice(data.data.length - 10, data.data.length) 
                : data.data;return {...data, data: modifiedData}
            case 'MAX': 
            default: return data;
        }
    }

    const adjustTimeRangeQuarter = (data) => {
        if (!data || data.length === 0) return [];
        let modifiedData;
        switch (selectedRange) {
            case '3Y':
                modifiedData = filterQuarterlyDataByYears(data.data, 3);
                return {...data, data: modifiedData}
            case '5Y': 
            modifiedData = filterQuarterlyDataByYears(data.data, 5);
                return {...data, data: modifiedData}
            case '10Y': 
            modifiedData = filterQuarterlyDataByYears(data.data, 10);
                return {...data, data: modifiedData}
            case 'MAX': 
            default: return data;
        }
    }

    const range = (y) => {
        const yMax = Math.max(...y);
        const yMin = Math.min(...y);
        const range = yMax - yMin
        if (yMin > 0) {
            const rangeMax = yMax * 1.2
            const rangeMin = 0;
            return [rangeMin, rangeMax]
        } else if (yMax < 0) {
            const rangeMax = 0
            const rangeMin = yMin * 1.2;
            return [rangeMin, rangeMax]
        } else {
            const rangeMax = yMax + range * 0.1
            const rangeMin = yMin - range * 0.1
            return [rangeMin, rangeMax]
        }
    }

    const extractAllValues = (data) => {
        return data.reduce((acc, item) => {
            Object.keys(item).forEach(key => {
                if (key !== "x") {
                    acc.push(item[key]);
                }
            });
            return acc;
        }, []);
    };
    
    useEffect(() => {
        if (cleanedData) {
            if (parentWidth / cleanedData.length < 80 ) {
                setIsSmallArea(true);
            } else {
                setIsSmallArea(false);
            }
        }
    }, [parentWidth, cleanedData])

    const handleLabelSettingClick = (newState) => {
        setState(newState);
    }

    const makeQuarterlyData = (annualData, quarterlyData) => {
        const accountCol = annualData.col;
        const modifiedData = {
            col: accountCol,
            companies: annualData.companies,
            currency: annualData.currency,
            dataCategory: annualData.dataCategory,
            graphType: annualData.graphType,
            name: annualData.name,
            unitX: annualData.unitX,
        }

        const xList = [];
        quarterlyData.map((series) => {
            series.map((data) => {
                xList.push(data.fiscal_year + ' ' + data.type_of_period)
            }) 
        })
        const uniqueSortedXList = Array.from(new Set(xList)).sort((a, b) => a - b);
        const mergedData = uniqueSortedXList.map((date) => {
            // 各日付に対応するデータを作成
            const rowData = { x: date }; // 初期化
        
            quarterlyData.forEach((series) => {
                // 各 series について、date に一致するデータを探す
                const foundData = series.find(
                    (data) => data.fiscal_year + ' ' + data.type_of_period === date
                );
                // series.secCode をキーとして値を追加 (データがない場合は null)
                if (series[0].fiscal_year + ' ' + series[0].type_of_period === date) {
                }
                rowData[series[0].sec_code] = foundData ? foundData[accountCol] : null;
            });
        
            return rowData;
        });
        modifiedData['data'] = mergedData;
        return modifiedData;
    }

    useEffect(() => {
        const fetchData = async () => {
            if (selectedPeriod === 'quarter') {
                const accountCol = data.col;
                const secCodes = data.companies.map((company) => company.secCode);
                
                // 非同期処理を並列で実行し、すべて完了するまで待つ
                try {
                    const fetchedData = await Promise.all(
                        secCodes.map((secCode) => fetchQuarterlyFinancialData(secCode, accountCol, navigate, location))
                    );
                    
                    const quarterlyData = fetchedData.map((data) => data.data); // すべてのデータが取得できた後にログ出力
                    const modifiedQuarterlyData = makeQuarterlyData(data, quarterlyData);
                    setQuarterlyData(modifiedQuarterlyData)
                    const adjustedQuarterlyData = adjustTimeRangeQuarter(modifiedQuarterlyData);
                    setModifiedData(adjustedQuarterlyData)
                    
                } catch (error) {
                    console.error("Error fetching quarterly financial data:", error);
                }
            }
        };
    
        fetchData(); // 非同期関数を呼び出す
    }, [selectedPeriod])

    if (!cleanedData) {
        return <Loading />
    }

    if (companies.length === 0) {
        return <Loading />
    }

    const handlePeriodChange = (period) => {
        setSelectPeriod(period)
    }

    const periodOptions = [
        { id: 'annual', label: '通期' },
        { id: 'quarter', label: '四半期' }
    ]
    
    return (
        <>
            <div className="analysis-menu-bar flex flex-wrap gap-2 items-center mb-4" >
                <div className='graph-title ml-2 text-xl bold text-gray-600'>{data.name}</div>
                {showSaveNotebookIcon && <div className=''><NotebookIcon chatHistoryId={chatHistoryId} onClick={handleBookmarkClick} /></div>}
                {<TimeRangeContainer timeRanges={timeRanges} handleTimeRangeSetting={handleTimeRangeSetting} selectedRange={selectedRange} />}
                {showTwoColumnSwitch && <TwoColumnSwitch options={periodOptions} onChange={handlePeriodChange}  />}
                <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick} labelState={state} /> 
            </div>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart
                    data={cleanedData}
                    margin={{ top: 20, right: 5, left: 5, bottom: 40 }}
                    barCategoryGap="20%"
                    barGap={5}
                >
                    <defs>
                        {stopColor.map((color, index) => (
                            <linearGradient id={`gradient${index}`} key={index} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color.start} />
                                <stop offset="95%" stopColor={color.end} />
                            </linearGradient>
                        ))}
                    </defs>
                    {((state === 'auto' && isSmallArea) || state==='hidden') && <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />}
                    <ReferenceLine y={0} stroke="lightgray" />
                    {((state === 'auto' && isSmallArea) || state==='hidden') &&
                        <YAxis tickFormatter={(value) => japaneseTickFormatter(value, '')}
                            tickCount={4}
                            domain={range(extractAllValues(cleanedData))}
                        >
                            <Label
                                position="top"
                                offset={20}
                                style={{ textAnchor: 'middle', fill: 'grey' }}
                            />
                        </YAxis>
                    }
                    <XAxis
                        dataKey="x"
                        tick={{ fontSize: 12, fill: '#000' }}>
                    </XAxis>
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: 'rgba(200, 200, 200, 0.5)' }}
                    />
                    <Legend verticalAlign="top" align="left" wrapperStyle={{ top: 0, display: "flex", flexWrap: "wrap" }} />
                    {companies.slice(0, MAX_NUM_OF_DATA).map((company, seriesIndex) => (
                        <Bar
                        key={company.secCode}
                        dataKey={company.secCode}
                        fill={`url(#gradient${seriesIndex % stopColor.length})`}  // グラデーションを適用
                        name={company.companyName}
                        label={((!isSmallArea && state==='auto') || state==='visible') ? <CustomLabel secCode={company.secCode} unit={data['unit']} /> : null }
                        radius={[1, 1, 0, 0]}
                        />
                    ))}
                    <Brush dataKey="x" height={15} stroke="lightgrey" />
                </BarChart>
            </ResponsiveContainer>  
        </>
    );
};

export default AnalysisBarGraph;
